import { withScope } from "@sentry/browser";
import { z } from "zod";

import {
  NablaRegionKnownValue,
  NablaRegionKnownValues,
} from "generated/account";
import { staticT as t } from "i18n";
import { getKnownValue } from "utils/enum";
import { apiUrl } from "utils/environment";
import { notifier } from "utils/notifier";
import versions from "versions.json";

import { jwtRegion } from "./jwt";
import {
  AnyEndpointDefinition,
  API_KIND_BASE_PATH,
  AUTHENTICATION_KIND_SELECTS_IDENTITY,
  EndpointAuthenticationKind,
  RequestContext,
  RequestPayload,
} from "./types";

/**
 * Queries an API endpoint and returns the validated response.
 * Throws if:
 * - the request fails,
 * - the request data doesn't match the expected schema,
 * - the response data doesn't match the expected schema.
 */
export const queryEndpoint = async <D extends AnyEndpointDefinition>(
  definition: D,
  payload: RequestPayload<D>,
  signal?: AbortSignal,
  forceRefresh = false,
): Promise<ApiResponse<z.infer<D["responseSchema"]>>> => {
  const {
    clientName = getDefaultClientName(),
    headers: extraHeaders,
    params,
    data,
    requestContext,
  } = payload;

  const path = `${API_KIND_BASE_PATH[definition.apiKind]}/${definition.path}`;
  const acceptLanguage = t("utils.network.accept_language");
  const codeVersion = versions.code_version.toString();

  // Even though the TypeScript compiler should prevent invalid shapes for the
  // request params and data, we still validate them at runtime to check rules
  // that the compiler can't enforce–e.g. the length of strings.
  const parsedParams = params
    ? definition.requestParamsSchema?.parse(params)
    : undefined;
  const parsedData = data
    ? definition.requestDataSchema?.parse(data)
    : undefined;

  const {
    token,
    explicitRegion,
    organizationId,
    currentIdentityUuid,
    regionByAccountEmail,
  } = await getRawRequestContext(
    definition.authenticationKind,
    requestContext,
    forceRefresh,
  );

  const headers = {
    "Accept-Language": acceptLanguage,
    "X-Nabla-Code-Version": codeVersion,
    "X-Nabla-Client-Name": clientName,
    ...(token ? { "X-Nabla-Authorization": `Bearer ${token}` } : {}),
    ...(organizationId ? { "X-Nabla-Organization-ID": organizationId } : {}),
    ...(currentIdentityUuid
      ? { "X-Nabla-Identity-Id": currentIdentityUuid }
      : {}),
    ...(regionByAccountEmail
      ? { "X-Nabla-Account-Email": regionByAccountEmail }
      : {}),
    ...extraHeaders,
  };

  const url = new URL(`${apiUrl(explicitRegion)}/${path}`);
  if (parsedParams) {
    for (const [key, value] of Object.entries(parsedParams)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    }
  }

  // `FormData` bodies must be passed as-is so the browser sets the `multipart/form-data` headers
  // itself; other bodies are sent as JSON.
  const isFormData = parsedData instanceof FormData;
  const body =
    parsedData === undefined
      ? undefined
      : isFormData
      ? parsedData
      : JSON.stringify(parsedData);

  let response: Response;
  try {
    response = await fetch(url, {
      method: parsedData ? "POST" : "GET",
      credentials: "include",
      headers: {
        ...(body !== undefined && !isFormData
          ? { "Content-Type": "application/json" }
          : {}),
        ...headers,
      },
      body,
      signal,
    });
  } catch (error) {
    if (!(error instanceof Error)) throw error;

    const apiError = new ApiError({
      message: error.message,
      cause: error,
      canceled: error.name === "AbortError" || (signal?.aborted ?? false),
    });

    if (!apiError.isCanceled) {
      withScope(() => {
        notifier.error({ sentry: { exception: apiError } });
      });
    }

    throw apiError;
  }

  if (!response.ok) {
    const apiError = new ApiError({
      message: `Request failed with status code ${response.status}`,
      response: {
        status: response.status,
        headers: response.headers,
        data: await parseErrorData(response),
      },
    });

    // Retry the request once if the error is a 401.
    if (apiError.isUnauthorized && !forceRefresh) {
      return queryEndpoint(definition, payload, signal, true);
    }

    if (!apiError.isUnauthorized) {
      withScope((scope) => {
        if (apiError.requestId) scope.setTag("request-id", apiError.requestId);
        notifier.error({ sentry: { exception: apiError } });
      });
    }

    throw apiError;
  }

  return {
    ...parseResponseMetadata(response.headers),
    data: definition.responseSchema.parse(await response.json()),
  };
};

/** Parses the body of an error response, tolerating non-JSON payloads. */
const parseErrorData = async (response: Response): Promise<ApiErrorDetails> => {
  try {
    return await response.clone().json();
  } catch {
    return {};
  }
};

export const getRawRequestContext = async (
  authenticationKind: EndpointAuthenticationKind,
  requestContext: RequestContext<EndpointAuthenticationKind>,
  forceRefresh = false,
) => {
  const accountAccessToken =
    "accountAccessToken" in requestContext
      ? await requestContext.accountAccessToken(forceRefresh)
      : undefined;
  const serverToken =
    "serverToken" in requestContext
      ? await requestContext.serverToken(forceRefresh)
      : undefined;

  const token = accountAccessToken ?? serverToken;

  const explicitRegion: NablaRegionKnownValue | undefined =
    token?.let(jwtRegion) ??
    ("region" in requestContext && // UNAUTHENTICATED_ALMIGHTY case.
    requestContext.region !== "CLOSEST_REGION"
      ? requestContext.region
      : undefined);

  const organizationId =
    "organizationId" in requestContext &&
    requestContext.organizationId.isNotBlank()
      ? requestContext.organizationId
      : "regionByOrganizationId" in requestContext &&
        requestContext.regionByOrganizationId.isNotBlank()
      ? requestContext.regionByOrganizationId
      : undefined;

  // Checked against the endpoint rather than the context's shape: a context
  // carrying an identity is still assignable to one that has none.
  const currentIdentityUuid =
    AUTHENTICATION_KIND_SELECTS_IDENTITY[authenticationKind] &&
    "currentIdentityUuid" in requestContext &&
    requestContext.currentIdentityUuid.isNotBlank()
      ? requestContext.currentIdentityUuid
      : undefined;

  const regionByAccountEmail =
    "regionByAccountEmail" in requestContext &&
    requestContext.regionByAccountEmail.isNotBlank()
      ? requestContext.regionByAccountEmail
      : undefined;

  return {
    token: token?.token,
    explicitRegion,
    organizationId,
    currentIdentityUuid,
    regionByAccountEmail,
  };
};

export type ApiResponse<Data> = { data: Data } & ApiResponseMetadata;

/** The parts of an error response the client cares about. */
type ApiErrorResponse = {
  status: number;
  data: ApiErrorDetails;
};

/**
 * Errors coming from the backend or from the network layer.
 *
 * We use a class instead of a plain object because it makes it easier to
 * check the type of the exception in a `catch` block using `instanceof`.
 */
export class ApiError extends Error implements ApiResponseMetadata {
  public requestId?: string;
  public region?: NablaRegionKnownValue;
  public minimumCodeVersion?: number;
  public cause?: Error;

  /** Present for HTTP errors (non-2xx), absent for network errors/aborts. */
  public readonly response?: ApiErrorResponse;
  private readonly canceled: boolean;

  constructor(params: {
    message: string;
    response?: ApiErrorResponse & { headers: Headers };
    canceled?: boolean;
    cause?: Error;
  }) {
    super(params.message);
    this.cause = params.cause;
    this.canceled = params.canceled ?? false;

    if (params.response) {
      this.response = {
        status: params.response.status,
        data: params.response.data,
      };
      const metadata = parseResponseMetadata(params.response.headers);
      this.requestId = metadata.requestId;
      this.region = metadata.region;
      this.minimumCodeVersion = metadata.minimumCodeVersion;
    }
  }

  get isUnauthorized() {
    return this.response?.status === 401;
  }
  get isCanceled() {
    return this.canceled;
  }
  get errorMessage() {
    return this.response?.data.message;
  }

  toJSON() {
    const { ...members } = this;
    return members;
  }
}

// Keep in sync with `RequestExceptionHandling.kt`.
type ApiErrorDetails = {
  message?: string;
  code?: number;
  name?: string;
};

type ApiResponseMetadata = {
  requestId?: string;
  region?: NablaRegionKnownValue;
  minimumCodeVersion?: number;
};

const parseResponseMetadata = (headers: Headers): ApiResponseMetadata => {
  const metadata: ApiResponseMetadata = {};

  const minCodeVersion = headers.get("X-Nabla-Minimum-Code-Version");
  if (minCodeVersion) metadata.minimumCodeVersion = parseInt(minCodeVersion);

  const requestId = headers.get("X-Request-Id");
  if (requestId) metadata.requestId = requestId;

  const region = getKnownValue(
    headers.get("X-Nabla-Region"),
    NablaRegionKnownValues,
  );
  if (region) metadata.region = region;

  return metadata;
};

const getDefaultClientName = () =>
  window.matchMedia("(display-mode: standalone)").matches
    ? "web-android"
    : window.matchMedia("(max-width: 1024px)").matches
    ? "web-mobile"
    : "web";
